import client from './client';
import { fetchAllPages } from './fetchAllPages';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toBoolean(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    if (text === 'true' || text === 'yes' || text === 'on') return true;
    if (text === 'false' || text === 'no' || text === 'off') return false;
  }
  return Boolean(value);
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(params).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function normalizePagination(source = {}) {
  if (!source || Array.isArray(source)) return null;

  const pagination = {};
  const total = toNumberOrNull(source.count ?? source.total ?? source.total_count ?? source.totalCount);
  const page = toNumberOrNull(source.page ?? source.current_page ?? source.currentPage);
  const pageSize = toNumberOrNull(source.page_size ?? source.per_page ?? source.perPage ?? source.limit);
  const lastPage = toNumberOrNull(
    source.last_page ??
    source.lastPage ??
    source.total_pages ??
    source.totalPages ??
    source.page_count ??
    source.pageCount
  );

  if (total !== null) pagination.count = total;
  if (page !== null) pagination.page = page;
  if (pageSize !== null) {
    pagination.per_page = pageSize;
    pagination.page_size = pageSize;
  }
  if (lastPage !== null) {
    pagination.last_page = lastPage;
    pagination.total_pages = lastPage;
  }
  if (source.next !== undefined) pagination.next = source.next;
  if (source.previous !== undefined) pagination.previous = source.previous;

  return Object.keys(pagination).length > 0 ? pagination : null;
}

function extractCollection(responseData, normalize) {
  const payload = unwrapResponse(responseData);
  const envelope =
    (Array.isArray(payload?.items) || Array.isArray(payload?.results) || payload?.pagination)
      ? payload
      : payload?.data ?? payload;

  // Handle every format the backend might return:
  //  { items: [...], pagination: {...} }   -> standard
  //  { results: [...], count: ... }        -> DRF-style pagination
  //  { data: [...] }                       -> direct array under data
  //  [...]                                 -> bare array
  const rawItems = Array.isArray(envelope?.items)
    ? envelope.items
    : Array.isArray(envelope?.results)
    ? envelope.results
    : Array.isArray(envelope)
    ? envelope
    : Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];
  const pagination = normalizePagination(envelope?.pagination ?? payload?.pagination ?? payload);

  return {
    items: rawItems.map(normalize),
    pagination,
  };
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (Array.isArray(candidate.results)) {
      const match = targetUuid
        ? candidate.results.find((item) => item?.uuid === targetUuid)
        : candidate.results[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.species_code ||
      candidate.breed_code ||
      candidate.purpose_code ||
      candidate.age_class_code
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage }) {
  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, { per_page: params.per_page ?? defaultPerPage, page: params.page ?? 1 }),
    });
    return extractCollection(data, normalize);
  }

  return {
    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }), { pageSize: params.per_page ?? defaultPerPage });
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      return extractItem(data, normalize);
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      return extractItem(data, normalize, uuid);
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      return data;
    },
  };
}

function normalizeSpecies(row = {}) {
  const speciesCode =
    row.species_code ??
    row.speciesCode ??
    row.code ??
    row.species_id ??
    row.speciesId ??
    row.slug ??
    '';

  const commonName =
    row.common_name_nigeria ??
    row.commonNameNigeria ??
    row.common_name ??
    row.commonName ??
    row.name ??
    '';

  return {
    uuid: row.uuid ?? row.id ?? row.species_id ?? null,
    id: row.id ?? row.uuid ?? row.species_id ?? null,
    species_code: speciesCode,
    speciesCode,
    common_name_nigeria: commonName,
    commonNameNigeria: commonName,
    scientific_name: row.scientific_name ?? row.scientificName ?? row.scientific ?? '',
    scientificName: row.scientificName ?? row.scientific_name ?? row.scientific ?? '',
    species_category: row.species_category ?? row.speciesCategory ?? row.category ?? '',
    speciesCategory: row.speciesCategory ?? row.species_category ?? row.category ?? '',
    sex_disaggregation_required: toBoolean(row.sex_disaggregation_required ?? row.sexDisaggregationRequired),
    sexDisaggregationRequired: toBoolean(row.sexDisaggregationRequired ?? row.sex_disaggregation_required),
    mandate_status: row.mandate_status ?? row.mandateStatus ?? '',
    mandateStatus: row.mandateStatus ?? row.mandate_status ?? '',
    mandate_decision_ref: row.mandate_decision_ref ?? row.mandateDecisionRef ?? null,
    mandateDecisionRef: row.mandateDecisionRef ?? row.mandate_decision_ref ?? null,
    is_validated: toBoolean(row.is_validated ?? row.isValidated),
    isValidated: toBoolean(row.isValidated ?? row.is_validated),
    created_at: row.created_at ?? row.createdAt ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
}

function buildSpeciesPayload(payload = {}) {
  return {
    species_code: trimOrNull(payload.species_code),
    common_name_nigeria: trimOrNull(payload.common_name_nigeria),
    scientific_name: trimOrNull(payload.scientific_name),
    species_category: trimOrNull(payload.species_category),
    sex_disaggregation_required: toBoolean(payload.sex_disaggregation_required),
    mandate_status: trimOrNull(payload.mandate_status),
  };
}

function normalizeBreed(row = {}) {
  const speciesUuid = row.species_uuid ?? row.species_id ?? row.speciesId ?? row.species_code ?? row.speciesCode ?? '';
  return {
    uuid: row.uuid ?? row.id ?? row.breed_id ?? null,
    id: row.id ?? row.uuid ?? row.breed_id ?? null,
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: row.species_code ?? row.speciesCode ?? '',
    speciesCode: row.speciesCode ?? row.species_code ?? '',
    breed_code: row.breed_code ?? row.breedCode ?? row.code ?? '',
    breedCode: row.breedCode ?? row.breed_code ?? row.code ?? '',
    breed_name: row.breed_name ?? row.breedName ?? row.name ?? '',
    breedName: row.breedName ?? row.breed_name ?? row.name ?? '',
    genetic_type: row.genetic_type ?? row.geneticType ?? '',
    geneticType: row.geneticType ?? row.genetic_type ?? '',
    primary_production_trait: row.primary_production_trait ?? row.primaryProductionTrait ?? '',
    primaryProductionTrait: row.primaryProductionTrait ?? row.primary_production_trait ?? '',
    created_at: row.created_at ?? row.createdAt ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
}

function buildBreedPayload(payload = {}) {
  return {
    species_uuid: trimOrNull(payload.species_uuid),
    breed_code: trimOrNull(payload.breed_code),
    breed_name: trimOrNull(payload.breed_name),
    genetic_type: trimOrNull(payload.genetic_type),
    primary_production_trait: trimOrNull(payload.primary_production_trait),
  };
}

function normalizeProductionPurpose(row = {}) {
  const speciesUuid = row.species_uuid ?? row.species_id ?? row.speciesId ?? row.species_code ?? row.speciesCode ?? '';
  return {
    uuid: row.uuid ?? row.id ?? row.purpose_id ?? null,
    id: row.id ?? row.uuid ?? row.purpose_id ?? null,
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: row.species_code ?? row.speciesCode ?? '',
    speciesCode: row.speciesCode ?? row.species_code ?? '',
    purpose_code: row.purpose_code ?? row.purposeCode ?? row.code ?? '',
    purposeCode: row.purposeCode ?? row.purpose_code ?? row.code ?? '',
    purpose_label: row.purpose_label ?? row.purposeLabel ?? row.label ?? '',
    purposeLabel: row.purposeLabel ?? row.purpose_label ?? row.label ?? '',
    seasonal_flag: toBoolean(row.seasonal_flag ?? row.seasonalFlag),
    seasonalFlag: toBoolean(row.seasonalFlag ?? row.seasonal_flag),
    created_at: row.created_at ?? row.createdAt ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
}

function buildProductionPurposePayload(payload = {}) {
  return {
    species_uuid: trimOrNull(payload.species_uuid),
    purpose_code: trimOrNull(payload.purpose_code),
    purpose_label: trimOrNull(payload.purpose_label),
    seasonal_flag: toBoolean(payload.seasonal_flag),
  };
}

function normalizeAgeClass(row = {}) {
  const speciesUuid = row.species_uuid ?? row.species_id ?? row.speciesId ?? row.species_code ?? row.speciesCode ?? '';
  return {
    uuid: row.uuid ?? row.id ?? row.age_class_id ?? null,
    id: row.id ?? row.uuid ?? row.age_class_id ?? null,
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: row.species_code ?? row.speciesCode ?? '',
    speciesCode: row.speciesCode ?? row.species_code ?? '',
    age_class_code: row.age_class_code ?? row.ageClassCode ?? row.code ?? '',
    ageClassCode: row.ageClassCode ?? row.age_class_code ?? row.code ?? '',
    age_class_label: row.age_class_label ?? row.ageClassLabel ?? row.label ?? '',
    ageClassLabel: row.ageClassLabel ?? row.age_class_label ?? row.label ?? '',
    age_range_description: row.age_range_description ?? row.ageRangeDescription ?? '',
    ageRangeDescription: row.ageRangeDescription ?? row.age_range_description ?? '',
    sort_order: toNumberOrNull(row.sort_order ?? row.sortOrder),
    sortOrder: toNumberOrNull(row.sortOrder ?? row.sort_order),
    created_at: row.created_at ?? row.createdAt ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
  };
}

function buildAgeClassPayload(payload = {}) {
  return {
    species_uuid: trimOrNull(payload.species_uuid),
    age_class_code: trimOrNull(payload.age_class_code),
    age_class_label: trimOrNull(payload.age_class_label),
    age_range_description: trimOrNull(payload.age_range_description),
    sort_order: toNumberOrNull(payload.sort_order),
  };
}

export const speciesApi = makeCollectionApi({
  basePath: '/v1/species',
  normalize: normalizeSpecies,
  buildPayload: buildSpeciesPayload,
  defaultPerPage: 50,
});

export const breedEcotypeApi = makeCollectionApi({
  basePath: '/v1/breeds',
  normalize: normalizeBreed,
  buildPayload: buildBreedPayload,
  defaultPerPage: 50,
});

export const productionPurposeApi = makeCollectionApi({
  basePath: '/v1/production-purposes',
  normalize: normalizeProductionPurpose,
  buildPayload: buildProductionPurposePayload,
  defaultPerPage: 100,
});

export const ageClassApi = makeCollectionApi({
  basePath: '/v1/age-classes',
  normalize: normalizeAgeClass,
  buildPayload: buildAgeClassPayload,
  defaultPerPage: 100,
});

export {
  buildQueryParams,
  buildSpeciesPayload,
  buildBreedPayload,
  buildProductionPurposePayload,
  buildAgeClassPayload,
  extractCollection,
  extractItem,
  normalizeSpecies,
  normalizeBreed,
  normalizeProductionPurpose,
  normalizeAgeClass,
};
